import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useInventory} from "../State/InventoryContext";
import {GameItems, SMELTER_RECIPES} from "../State/GameData";
import {ASSEMBLY_RECIPE_LIST, AssemblyRecipe} from "../State/AssemblyRecipes";
import {calculateSmelterOutputPerSecond, countSetupsUsingSource} from "../utils/smelterCalculations";
import {createRecipeLookup, generateNodeId, getNodeOutputItem, nodeHasIngredients} from "./AssemblyHelpers";
import {AssemblyConnections} from "./AssemblyConnections";
import {AssemblyPalette} from "./AssemblyPalette";
import {
    AssemblyConnection,
    AssemblyNode,
    AssemblyNodeType,
    AssemblySetup,
    ConnectionDraft,
    InventoryPulse,
} from "./AssemblyTypes";
import {AssemblyNodeCard} from "./AssemblyNodeCard";
import {L} from "./AssemblyStyles";
import {ItemIcon} from "../SharedComponents/ItemIcon";

const sourcePalette = [
    {itemId: "ironplate", color: "#facc15"},
    {itemId: "copperplate", color: "#fb923c"},
];

const defaultRecipeId = ASSEMBLY_RECIPE_LIST[0]?.id;
const ASSEMBLER_POWER_USE = 20;
const OVERCLOCK_SPEED = 2;
const OVERCLOCK_POWER = 4;

const recipeLookup = createRecipeLookup();

export function AssemblyPlanner() {
    const [searchParams] = useSearchParams();
    const {inventory, getItemQuantity, setExternalPowerDemand, updateAssemblySetup, addAssemblySetup, deleteAssemblySetup} = useInventory();
    const hasAssemblerBuilt = getItemQuantity("circuitAssembler") > 0;
    const gridPower = useMemo(() => ({
        demand: inventory.powerStats?.demand ?? 0,
        supply: inventory.powerStats?.supply ?? 0,
        externalDemand: inventory.powerStats?.externalDemand ?? 0,
    }), [inventory.powerStats]);

    const setups = inventory.assemblySetups;

    const [activeSetupId, setActiveSetupId] = useState<string | null>(null);
    const [inventoryPulses, setInventoryPulses] = useState<Record<string, InventoryPulse[]>>({});

    const mountedRef = useRef(true);

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    useEffect(() => {
        const setupParam = searchParams.get('setup');
        if (setupParam && setups.find(s => s.id === setupParam)) {
            setActiveSetupId(setupParam);
        } else if (!activeSetupId && setups.length) {
            setActiveSetupId(setups[0].id);
        }
        // Only run when URL params change or setup count changes, not on every setups reference change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, activeSetupId, setups.length]);

    const activeSetup = setups.find(setup => setup.id === activeSetupId) ?? null;

    const addSetup = () => {
        const newSetup: AssemblySetup = {
            id: generateNodeId(),
            name: `Setup ${setups.length + 1}`,
            nodes: [{
                id: generateNodeId(),
                type: "inventory",
                label: "Inventory",
                position: {x: 90, y: 50},
            }],
            connections: [],
        };
        addAssemblySetup(newSetup);
        setActiveSetupId(newSetup.id);
    };

    const deleteSetup = (setupId: string) => {
        deleteAssemblySetup(setupId);
        // If we deleted the active setup, switch to another one or null
        if (setupId === activeSetupId) {
            const filtered = setups.filter(s => s.id !== setupId);
            setActiveSetupId(filtered.length > 0 ? filtered[0].id : null);
        }
    };

    const updateActiveSetup = useCallback((updater: (setup: AssemblySetup) => AssemblySetup) => {
        if (!activeSetup) return;
        updateAssemblySetup(activeSetup.id, updater);
    }, [activeSetup, updateAssemblySetup]);

    const handleDrop = (ev: React.DragEvent<HTMLDivElement>) => {
        ev.preventDefault();
        if (!activeSetup) return;
        const type = ev.dataTransfer.getData("node-type") as AssemblyNodeType | "";
        const itemId = ev.dataTransfer.getData("item-id") || undefined;
        if (!type) return;
        const rect = ev.currentTarget.getBoundingClientRect();
        const x = ((ev.clientX - rect.left) / rect.width) * 100;
        const y = ((ev.clientY - rect.top) / rect.height) * 100;
        const newNode: AssemblyNode = {
            id: generateNodeId(),
            label: type === "assembler" ? "Assembler" : GameItems[itemId ?? ""]?.name ?? "Source",
            type,
            itemId,
            recipeId: type === "assembler" ? (defaultRecipeId ?? undefined) : undefined,
            position: {x, y},
            running: true,
            overclocked: false,
            progressMs: 0,
        };
        updateActiveSetup(setup => ({...setup, nodes: [...setup.nodes, newNode]}));
    };

    const createConnection = (fromId: string, toId: string) => {
        if (!activeSetup) return;
        const fromNode = activeSetup.nodes.find(node => node.id === fromId);
        const toNode = activeSetup.nodes.find(node => node.id === toId);
        if (!fromNode || !toNode) return;
        const itemId = getNodeOutputItem(fromNode, recipeLookup);
        if (!itemId) return;
        const key = `${fromId}-${toId}-${itemId}`;
        if (activeSetup.connections.some(conn => `${conn.fromId}-${conn.toId}-${conn.itemId}` === key)) {
            return;
        }
        const newConnection: AssemblyConnection = {id: generateNodeId(), fromId, toId, itemId};
        updateActiveSetup(setup => ({...setup, connections: [...setup.connections, newConnection]}));
    };

    const removeConnection = (connectionId: string) => {
        updateActiveSetup(setup => ({
            ...setup,
            connections: setup.connections.filter(conn => conn.id !== connectionId),
        }));
    };

    const setNodeRecipe = (nodeId: string, recipeId: string) => {
        updateActiveSetup(setup => ({
            ...setup,
            nodes: setup.nodes.map(node => node.id === nodeId ? {...node, recipeId} : node),
            connections: setup.connections.filter(conn => conn.fromId !== nodeId),
        }));
    };

    const toggleNodeFlag = (nodeId: string, field: "running" | "overclocked") => {
        updateActiveSetup(setup => ({
            ...setup,
            nodes: setup.nodes.map(node => node.id === nodeId ? {...node, [field]: !node[field]} : node),
        }));
    };

    const deleteNode = (nodeId: string) => {
        updateActiveSetup(setup => ({
            ...setup,
            nodes: setup.nodes.filter(node => node.id !== nodeId),
            connections: setup.connections.filter(conn => conn.fromId !== nodeId && conn.toId !== nodeId),
        }));
    };

    const [connectionDraft, setConnectionDraft] = useState<ConnectionDraft>({fromId: "", toId: ""});
    const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
    const boardRef = useRef<HTMLDivElement | null>(null);
    const [boardSize, setBoardSize] = useState<{ width: number, height: number }>({width: 0, height: 0});
    const [zoom, setZoom] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [isDraggingConnection, setIsDraggingConnection] = useState(false);
    const [dragConnectionFrom, setDragConnectionFrom] = useState<string | null>(null);
    const [dragLineEnd, setDragLineEnd] = useState<{ x: number, y: number } | null>(null);
    const [highlightedInputNode, setHighlightedInputNode] = useState<string | null>(null);

    useEffect(() => {
        const measure = () => {
            if (!boardRef.current) return;
            const rect = boardRef.current.getBoundingClientRect();
            setBoardSize(prev => {
                // Only update if size actually changed to prevent infinite loops
                if (Math.abs(prev.width - rect.width) > 1 || Math.abs(prev.height - rect.height) > 1) {
                    return {width: rect.width, height: rect.height};
                }
                return prev;
            });
        };

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    useEffect(() => {
        if (activeSetup?.id && boardRef.current) {
            const rect = boardRef.current.getBoundingClientRect();
            setBoardSize(prev => {
                if (Math.abs(prev.width - rect.width) > 1 || Math.abs(prev.height - rect.height) > 1) {
                    return {width: rect.width, height: rect.height};
                }
                return prev;
            });
        }
    }, [activeSetup?.id]);

    const connectionOptions = useMemo(() => ({
        from: activeSetup?.nodes.filter(node => getNodeOutputItem(node, recipeLookup)) ?? [],
        to: activeSetup?.nodes.filter(node => node.type !== "source") ?? [],
    }), [activeSetup]);

    const assemblyStats = useMemo(() => {
        if (!activeSetup) {
            return {powerRatio: 1, assemblyDemand: 0, totalDemand: gridPower.demand};
        }
        const assemblyDemand = activeSetup.nodes.reduce((sum, node) => {
            if (node.type !== "assembler" || node.running === false) return sum;
            return sum + ASSEMBLER_POWER_USE * (node.overclocked ? OVERCLOCK_POWER : 1);
        }, 0);
        const baseDemand = Math.max(0, gridPower.demand - gridPower.externalDemand);
        const totalDemand = baseDemand + assemblyDemand;
        const powerRatio = totalDemand === 0 ? 1 : Math.min(1, gridPower.supply / totalDemand);
        return {powerRatio, assemblyDemand, totalDemand};
    }, [activeSetup, gridPower.demand, gridPower.externalDemand, gridPower.supply]);

    useEffect(() => {
        setExternalPowerDemand(assemblyStats.assemblyDemand);
        return () => setExternalPowerDemand(0);
        // setExternalPowerDemand is from context and should be stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assemblyStats.assemblyDemand]);

    const powerOffline = assemblyStats.totalDemand > 0 && gridPower.supply <= 0;
    const powerLimited = assemblyStats.powerRatio < 1 && gridPower.supply > 0;

    // Calculate smelter field statistics for the palette
    const smelterStats = useMemo(() => {
        const stats = new Map<string, {totalOutputPerSecond: number; usageCount: number; instanceCount: number}>();

        sourcePalette.forEach(source => {
            // Use shared utility for smelter output calculation
            const totalOutputPerSecond = calculateSmelterOutputPerSecond(inventory.smelterState, source.itemId);

            // Use shared utility for usage count
            const usageCount = countSetupsUsingSource(setups, source.itemId);

            // Count instances only in active setup for display purposes
            let instanceCount = 0;
            if (activeSetup) {
                instanceCount = activeSetup.nodes.filter(n => n.type === "source" && n.itemId === source.itemId).length;
            }

            stats.set(source.itemId, {totalOutputPerSecond, usageCount, instanceCount});
        });

        return stats;
    }, [inventory.smelterState, activeSetup, setups]);

    const nodeViews = useMemo(() => {
        const map = new Map<string, {
            ready: boolean;
            recipe?: AssemblyRecipe;
            outputPerSecond: number;
            maxOutputPerSecond: number;
            powerUse: number;
            fulfillment: number;
            status: string;
            active: boolean;
            powerLimited: boolean;
            powerOffline: boolean;
            inputLimited?: boolean;
            ingredientInputRates: Array<{itemId: string; quantity: number; availableInput: number; maxInput: number}>;
        }>();
        if (!activeSetup) return map;

        // Initialize all nodes with their ideal output (ignoring input limitations)
        const outputPerSecondMap = new Map<string, number>();

        activeSetup.nodes.forEach(node => {
            const recipe = node.recipeId ? recipeLookup[node.recipeId] : undefined;
            const baseSpeed = node.type === "assembler" && node.overclocked ? OVERCLOCK_SPEED : 1;
            const canRun = node.type === "assembler" && node.running !== false && !!recipe && nodeHasIngredients(activeSetup, node, recipeLookup);

            // Calculate ideal output (what the node would produce with unlimited inputs)
            let idealOutput = 0;
            if (node.type === "source" && node.itemId) {
                // Sources share capacity across all setups
                // Each setup gets an equal share of the total output
                const stats = smelterStats.get(node.itemId);
                if (stats && stats.usageCount > 0) {
                    // Calculate allocation per setup
                    const allocationPerSetup = stats.totalOutputPerSecond / stats.usageCount;

                    // Count how many source nodes of this type exist in the current setup
                    const sourceNodesInSetup = activeSetup.nodes.filter(
                        n => n.type === "source" && n.itemId === node.itemId
                    ).length;

                    // Divide the setup allocation among all source nodes in this setup
                    idealOutput = sourceNodesInSetup > 0 ? allocationPerSetup / sourceNodesInSetup : 0;
                } else if (stats) {
                    idealOutput = stats.totalOutputPerSecond;
                }
            } else if (canRun && assemblyStats.powerRatio > 0 && recipe) {
                idealOutput = (recipe.result.quantity / (recipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio;
            }
            outputPerSecondMap.set(node.id, idealOutput);
        });

        // Iteratively adjust outputs based on input availability and consumption (max 10 iterations)
        for (let iteration = 0; iteration < 10; iteration++) {
            let changed = false;

            // Forward pass: limit outputs based on available inputs
            activeSetup.nodes.forEach(node => {
                if (node.type !== "assembler") return;

                const recipe = node.recipeId ? recipeLookup[node.recipeId] : undefined;
                if (!recipe || node.running === false) return;

                const currentOutput = outputPerSecondMap.get(node.id) ?? 0;
                if (currentOutput === 0) return;

                // Calculate base output rate at full speed with current power
                const baseSpeed = node.overclocked ? OVERCLOCK_SPEED : 1;
                const baseOutputRate = (recipe.result.quantity / (recipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio;

                // Calculate available input throughput for each ingredient
                let maxPossibleOutput = baseOutputRate;

                recipe.ingredients.forEach(ingredient => {
                    // Find all incoming connections for this ingredient
                    const incomingForIngredient = activeSetup.connections.filter(
                        conn => conn.toId === node.id && conn.itemId === ingredient.itemId
                    );

                    // Sum up available throughput from all sources of this ingredient
                    const availableThroughput = incomingForIngredient.reduce((sum, conn) => {
                        const sourceOutput = outputPerSecondMap.get(conn.fromId) ?? 0;
                        return sum + sourceOutput;
                    }, 0);

                    // Calculate how much output we can produce with this ingredient's availability
                    // ingredient.quantity is consumed per recipe cycle
                    const ingredientConsumptionRate = (ingredient.quantity / (recipe.durationMs / 1000));
                    const maxConsumptionRate = ingredientConsumptionRate * baseSpeed * assemblyStats.powerRatio;

                    // Scale output based on available input vs required input
                    const possibleOutputFromThisIngredient = availableThroughput === 0
                        ? 0
                        : Math.min(baseOutputRate, (availableThroughput / maxConsumptionRate) * baseOutputRate);

                    maxPossibleOutput = Math.min(maxPossibleOutput, possibleOutputFromThisIngredient);
                });

                // Update if output changed significantly
                if (Math.abs(maxPossibleOutput - currentOutput) > 0.001) {
                    outputPerSecondMap.set(node.id, maxPossibleOutput);
                    changed = true;
                }
            });

            // Backward pass: limit outputs based on consumer demand
            activeSetup.nodes.forEach(node => {
                if (node.type === "inventory") return; // Inventory can consume unlimited
                if (node.type === "source") return; // Sources are already allocated per assembly

                const currentOutput = outputPerSecondMap.get(node.id) ?? 0;
                if (currentOutput === 0) return;

                const recipe = node.recipeId ? recipeLookup[node.recipeId] : undefined;
                if (!recipe && node.type === "assembler") return;

                // Find all outgoing connections
                const outgoingConnections = activeSetup.connections.filter(conn => conn.fromId === node.id);
                if (outgoingConnections.length === 0) return; // No consumers

                // Calculate total consumption from all consumers
                let totalConsumption = 0;

                outgoingConnections.forEach(conn => {
                    const consumer = activeSetup.nodes.find(n => n.id === conn.toId);
                    if (!consumer) return;

                    if (consumer.type === "inventory") {
                        // Inventory can consume unlimited
                        totalConsumption = Infinity;
                        return;
                    }

                    if (consumer.type === "assembler") {
                        const consumerRecipe = consumer.recipeId ? recipeLookup[consumer.recipeId] : undefined;
                        if (!consumerRecipe || consumer.running === false) return;

                        const consumerOutput = outputPerSecondMap.get(consumer.id) ?? 0;
                        if (consumerOutput === 0) return;

                        // Find the ingredient that matches our output
                        const ingredient = consumerRecipe.ingredients.find(ing => ing.itemId === conn.itemId);
                        if (!ingredient) return;

                        // Calculate consumption rate based on consumer's actual output
                        const baseSpeed = consumer.overclocked ? OVERCLOCK_SPEED : 1;
                        const consumptionRate = (ingredient.quantity / (consumerRecipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio;

                        // Scale consumption by actual output ratio
                        const consumerIdealOutput = (consumerRecipe.result.quantity / (consumerRecipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio;
                        const outputRatio = consumerIdealOutput > 0 ? consumerOutput / consumerIdealOutput : 0;

                        totalConsumption += consumptionRate * outputRatio;
                    }
                });

                // Limit output to total consumption (unless there's an inventory consumer)
                if (totalConsumption !== Infinity && totalConsumption < currentOutput) {
                    if (Math.abs(totalConsumption - currentOutput) > 0.001) {
                        outputPerSecondMap.set(node.id, totalConsumption);
                        changed = true;
                    }
                }
            });

            // If nothing changed, we've reached a stable state
            if (!changed) break;
        }

        // Now build the final map with all node information
        activeSetup.nodes.forEach(node => {
            const recipe = node.recipeId ? recipeLookup[node.recipeId] : undefined;
            const ready = node.type === "assembler" ? nodeHasIngredients(activeSetup, node, recipeLookup) : true;
            const baseSpeed = node.type === "assembler" && node.overclocked ? OVERCLOCK_SPEED : 1;
            const fulfillment = Math.round(assemblyStats.powerRatio * 100);
            const canRun = node.type === "assembler" && node.running !== false && !!recipe && ready;
            const powerLimitedNode = canRun && assemblyStats.powerRatio < 1;
            const powerOfflineNode = canRun && gridPower.supply <= 0;
            const hasOutputConnection = activeSetup.connections.some(conn => conn.fromId === node.id);

            // Get the calculated output from our throughput calculation
            let outputPerSecond = outputPerSecondMap.get(node.id) ?? 0;

            // Calculate ideal output for comparison
            const idealOutput = canRun && assemblyStats.powerRatio > 0 && recipe
                ? (recipe.result.quantity / (recipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio
                : 0;

            // Check if node is input-limited
            const inputLimited = canRun && outputPerSecond < idealOutput * 0.99;

            const powerUse = node.type === "assembler" && node.running !== false && outputPerSecond > 0
                ? ASSEMBLER_POWER_USE * (node.overclocked ? OVERCLOCK_POWER : 1) * assemblyStats.powerRatio * (outputPerSecond / (idealOutput || 1))
                : 0;

            let status: string;
            if (node.type !== "assembler") {
                status = node.type === "source" ? "Source" : "Inventory";
            } else if (node.running === false) {
                status = "Paused";
            } else if (!recipe) {
                status = "Select recipe";
            } else if (!ready) {
                status = "Missing ingredients";
            } else if (powerOfflineNode) {
                status = "No power";
            } else if (!hasOutputConnection) {
                status = "Output not consumed";
            } else if (inputLimited) {
                status = "Input limited";
            } else if (powerLimitedNode) {
                status = `Power limited (${fulfillment}%)`;
            } else if (outputPerSecond === 0) {
                status = "Idle";
            } else {
                status = "Running";
            }

            // Calculate ingredient input rates (available vs max)
            const ingredientInputRates = recipe?.ingredients.map(ingredient => {
                // Find available input from connections
                const incomingConnections = activeSetup.connections.filter(
                    conn => conn.toId === node.id && conn.itemId === ingredient.itemId
                );
                const availableInput = incomingConnections.reduce((sum, conn) => {
                    const sourceOutput = outputPerSecondMap.get(conn.fromId) ?? 0;
                    return sum + sourceOutput;
                }, 0);

                // Max consumption rate at full speed (no power/overclock modifiers)
                const maxConsumptionRate = ingredient.quantity / (recipe.durationMs / 1000);

                return {
                    itemId: ingredient.itemId,
                    quantity: ingredient.quantity,
                    availableInput,
                    maxInput: maxConsumptionRate,
                };
            }) ?? [];

            // Check if production occurred recently (within last 1500ms for pulse animation)
            const recentlyProduced = node.lastProductionTime && (Date.now() - node.lastProductionTime) < 1500;

            map.set(node.id, {
                ready,
                recipe,
                outputPerSecond,
                maxOutputPerSecond: idealOutput,
                powerUse,
                fulfillment,
                status,
                active: !!recentlyProduced,
                powerLimited: powerLimitedNode,
                powerOffline: powerOfflineNode,
                inputLimited,
                ingredientInputRates,
            });
        });

        return map;
        // nodeHasIngredients is a stable imported function, not a dependency
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSetup, assemblyStats.powerRatio, gridPower.supply, smelterStats]);

    const connectionLines = useMemo(() => {
        if (!activeSetup || !boardSize.width || !boardSize.height) return [] as Array<{ id: string, d: string, active: boolean, speed: number, labelX: number, labelY: number }>;
        return activeSetup.connections.map(conn => {
            const fromNode = activeSetup.nodes.find(node => node.id === conn.fromId);
            const toNode = activeSetup.nodes.find(node => node.id === conn.toId);
            const fromView = nodeViews.get(conn.fromId);
            const toView = nodeViews.get(conn.toId);
            if (!fromNode || !toNode) return {id: conn.id, d: "", active: false, speed: 0, labelX: 0, labelY: 0};
            const x1 = (fromNode.position.x / 100) * boardSize.width;
            const y1 = (fromNode.position.y / 100) * boardSize.height;
            const x2 = (toNode.position.x / 100) * boardSize.width;
            const y2 = (toNode.position.y / 100) * boardSize.height;
            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2 - 15; // Slightly above the line
            const curve = `M ${x1} ${y1} Q ${midX} ${y1 - 30} ${x2} ${y2}`;

            // Determine if resources are actually flowing through this connection
            let active = false;

            if (toNode.type === "assembler" && toNode.recipeId) {
                // Input connection TO assembler - check if buffer can accept more resources
                const recipe = recipeLookup[toNode.recipeId];
                if (recipe) {
                    const ingredient = recipe.ingredients.find(ing => ing.itemId === conn.itemId);
                    if (ingredient) {
                        const bufferCapacity = ingredient.quantity * 2;
                        const currentBuffer = toNode.inputBuffers?.[conn.itemId] ?? 0;
                        const bufferNotFull = currentBuffer < bufferCapacity;

                        if (fromNode.type === "source") {
                            // Source → Assembler: animate if source is producing AND buffer not full
                            active = bufferNotFull && (fromView?.outputPerSecond ?? 0) > 0;
                        } else if (fromNode.type === "assembler") {
                            // Assembler → Assembler: animate if source has output buffer AND target buffer not full
                            const sourceOutputBuffer = fromNode.outputBuffer?.[conn.itemId] ?? 0;
                            active = bufferNotFull && sourceOutputBuffer > 0;
                        } else if (fromNode.type === "inventory") {
                            // Inventory → Assembler: animate if buffer not full (inventory assumed to have items)
                            active = bufferNotFull;
                        }
                    }
                }
            } else if (toNode.type === "inventory") {
                // Output connection TO inventory
                if (fromNode.type === "assembler") {
                    // Assembler → Inventory: animate AFTER pulse finishes (1.5s pulse + 1.5s output = 3.0s total)
                    const outputBuffer = fromNode.outputBuffer?.[conn.itemId] ?? 0;
                    const lastTransfer = fromNode.lastInventoryTransferTime?.[conn.itemId] ?? 0;
                    const timeSinceTransfer = lastTransfer > 0 ? (Date.now() - lastTransfer) : Infinity;

                    // Show output animation if:
                    // 1. Output buffer has items (ongoing transfer), OR
                    // 2. Transfer happened within last 3.0s AND pulse is done (>1.5s since transfer)
                    // Extended to 1.5s window (was 0.5s) to ensure render cycles catch it
                    const showAfterPulse = timeSinceTransfer >= 1500 && timeSinceTransfer < 3000;
                    active = outputBuffer > 0 || showAfterPulse;
                } else if (fromNode.type === "source") {
                    // Source → Inventory: animate if source is producing
                    active = (fromView?.outputPerSecond ?? 0) > 0;
                }
            }

            // Calculate throughput per connection based on actual consumption
            let speed = 0;
            const fromOutput = fromView?.outputPerSecond ?? 0;

            if (toNode.type === "inventory") {
                // Inventory connection gets whatever is left after assembler consumption
                const connectionsFromSource = activeSetup.connections.filter(c => c.fromId === conn.fromId);
                let totalAssemblerConsumption = 0;

                // Sum up consumption from all assembler connections from this source
                connectionsFromSource.forEach(c => {
                    const consumer = activeSetup.nodes.find(n => n.id === c.toId);
                    if (consumer?.type === "assembler" && consumer.recipeId) {
                        const consumerView = nodeViews.get(consumer.id);
                        const consumerRecipe = recipeLookup[consumer.recipeId];
                        if (consumerRecipe && consumerView) {
                            // Find the ingredient that matches this connection's item
                            const ingredient = consumerRecipe.ingredients.find(ing => ing.itemId === c.itemId);
                            if (ingredient) {
                                // Calculate actual consumption rate
                                const baseSpeed = consumer.overclocked ? OVERCLOCK_SPEED : 1;
                                const consumptionRate = (ingredient.quantity / (consumerRecipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio;
                                // Scale by actual output ratio
                                const idealOutput = (consumerRecipe.result.quantity / (consumerRecipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio;
                                const outputRatio = idealOutput > 0 ? (consumerView.outputPerSecond / idealOutput) : 0;
                                totalAssemblerConsumption += consumptionRate * outputRatio;
                            }
                        }
                    }
                });

                // Inventory gets the remainder
                speed = Math.max(0, fromOutput - totalAssemblerConsumption);
            } else if (toNode.type === "assembler" && toNode.recipeId) {
                // Assembler connection gets its actual consumption
                const consumerRecipe = recipeLookup[toNode.recipeId];
                if (consumerRecipe && toView) {
                    const ingredient = consumerRecipe.ingredients.find(ing => ing.itemId === conn.itemId);
                    if (ingredient) {
                        const baseSpeed = toNode.overclocked ? OVERCLOCK_SPEED : 1;
                        const consumptionRate = (ingredient.quantity / (consumerRecipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio;
                        const idealOutput = (consumerRecipe.result.quantity / (consumerRecipe.durationMs / 1000)) * baseSpeed * assemblyStats.powerRatio;
                        const outputRatio = idealOutput > 0 ? (toView.outputPerSecond / idealOutput) : 0;
                        speed = consumptionRate * outputRatio;
                    }
                }
            }

            return {id: conn.id, d: curve, active, speed, labelX: midX, labelY: midY};
        }).filter(line => line.d);
    }, [activeSetup, boardSize.height, boardSize.width, nodeViews, assemblyStats.powerRatio]);

    const throughputTotals = useMemo(() => {
        const totals = new Map<string, number>();
        if (!activeSetup) return totals;
        activeSetup.nodes.forEach(node => {
            const view = nodeViews.get(node.id);
            if (!view?.recipe || view.outputPerSecond <= 0) return;
            const itemId = view.recipe.result.itemId;
            totals.set(itemId, (totals.get(itemId) ?? 0) + view.outputPerSecond);
        });
        return totals;
    }, [activeSetup, nodeViews]);

    const moveNode = useCallback((nodeId: string, x: number, y: number) => {
        updateActiveSetup(setup => ({
            ...setup,
            nodes: setup.nodes.map(node => node.id === nodeId ? {
                ...node,
                position: {x, y},
            } : node),
        }));
    }, [updateActiveSetup]);

    const handleBoardMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (isPanning) {
            const dx = event.clientX - panStart.x;
            const dy = event.clientY - panStart.y;
            setPanOffset({ x: panOffset.x + dx, y: panOffset.y + dy });
            setPanStart({ x: event.clientX, y: event.clientY });
            return;
        }
        if (isDraggingConnection && boardRef.current && boardSize.width > 0) {
            const rect = boardRef.current.getBoundingClientRect();
            const rawX = event.clientX - rect.left;
            const rawY = event.clientY - rect.top;
            // Convert to SVG coordinate space (accounting for zoom and pan)
            const x = (rawX - panOffset.x) / zoom;
            const y = (rawY - panOffset.y) / zoom;
            // Convert to viewBox coordinates
            const svgX = (x / rect.width) * boardSize.width;
            const svgY = (y / rect.height) * boardSize.height;
            setDragLineEnd({ x: svgX, y: svgY });
            return;
        }
        if (!draggingNodeId || !boardRef.current) return;
        const rect = boardRef.current.getBoundingClientRect();
        const x = ((event.clientX - rect.left - panOffset.x) / (rect.width * zoom)) * 100;
        const y = ((event.clientY - rect.top - panOffset.y) / (rect.height * zoom)) * 100;
        moveNode(draggingNodeId, x, y);
    };

    const stopDragging = () => {
        setDraggingNodeId(null);
        setIsPanning(false);
        if (isDraggingConnection) {
            if (highlightedInputNode && dragConnectionFrom) {
                createConnection(dragConnectionFrom, highlightedInputNode);
            }
            setIsDraggingConnection(false);
            setDragConnectionFrom(null);
            setDragLineEnd(null);
            setHighlightedInputNode(null);
        }
    };

    const startDragging = (event: React.MouseEvent, nodeId: string) => {
        const target = event.target as HTMLElement;
        // Don't start dragging if clicking on interactive elements or connection handles
        if (target.closest("select, button, input, label") || target.closest('[data-connection-handle]')) {
            return;
        }
        // Prevent connection dragging when node dragging starts
        if (isDraggingConnection) {
            return;
        }
        setDraggingNodeId(nodeId);
    };

    const handleBoardMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === boardRef.current || (event.target as HTMLElement).tagName === 'svg' || (event.target as HTMLElement).tagName === 'path') {
            setIsPanning(true);
            setPanStart({ x: event.clientX, y: event.clientY });
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(3, prev * 1.2));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(0.5, prev / 1.2));
    };

    const handleStartConnection = (event: React.MouseEvent, nodeId: string) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDraggingConnection(true);
        setDragConnectionFrom(nodeId);
        if (boardRef.current && boardSize.width > 0) {
            const rect = boardRef.current.getBoundingClientRect();
            const rawX = event.clientX - rect.left;
            const rawY = event.clientY - rect.top;
            // Convert to SVG coordinate space
            const x = (rawX - panOffset.x) / zoom;
            const y = (rawY - panOffset.y) / zoom;
            // Convert to viewBox coordinates
            const svgX = (x / rect.width) * boardSize.width;
            const svgY = (y / rect.height) * boardSize.height;
            setDragLineEnd({ x: svgX, y: svgY });
        }
    };

    const handleMouseEnterInput = (nodeId: string) => {
        if (isDraggingConnection && dragConnectionFrom !== nodeId) {
            setHighlightedInputNode(nodeId);
        }
    };

    const handleMouseLeaveInput = (nodeId: string) => {
        if (highlightedInputNode === nodeId) {
            setHighlightedInputNode(null);
        }
    };

    return <L.Page>
        <AssemblyPalette
            sourcePalette={sourcePalette}
            smelterStats={smelterStats}
            onAddSetup={addSetup}
            hasAssemblerBuilt={hasAssemblerBuilt}
        />
        <div>
            <L.BoardShell>
                <L.BoardHeader>
                    <L.BoardTitle>
                        <span>Assembly setups</span>
                        <L.Hint>Connect arrows from sources to assemblers, then to the inventory icon.</L.Hint>
                    </L.BoardTitle>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                        <L.SetupSelect value={activeSetupId ?? ""} onChange={e => setActiveSetupId(e.target.value)}>
                            {!activeSetupId && <option value="">Select a setup</option>}
                            {setups.map(setup => <option key={setup.id} value={setup.id}>{setup.name}</option>)}
                        </L.SetupSelect>
                        {activeSetup && (
                            <L.Button
                                onClick={() => deleteSetup(activeSetup.id)}
                                title="Delete current setup"
                                style={{background: '#fee2e2', borderColor: '#dc2626', color: '#dc2626'}}
                            >
                                🗑️ Delete
                            </L.Button>
                        )}
                    </div>
                </L.BoardHeader>
                <L.PowerBadge>
                    <span>Assembly power draw: {assemblyStats.assemblyDemand.toFixed(1)} kW</span>
                    <span>Grid fulfilment: {Math.round(assemblyStats.powerRatio * 100)}%</span>
                    <span>Supply: {gridPower.supply.toFixed(1)} kW / Demand: {assemblyStats.totalDemand.toFixed(1)} kW</span>
                </L.PowerBadge>
                <L.BoardCanvas ref={boardRef}
                               onDragOver={ev => ev.preventDefault()}
                               onDrop={handleDrop}
                               onMouseDown={handleBoardMouseDown}
                               onMouseMove={handleBoardMouseMove}
                               onMouseUp={stopDragging}
                               onMouseLeave={stopDragging}
                               style={{cursor: isPanning ? 'grabbing' : draggingNodeId ? 'grabbing' : 'default'}}>
                    <div style={{
                        transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
                        transformOrigin: '0 0',
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        pointerEvents: 'none',
                    }}>
                    {boardSize.width > 0 && boardSize.height > 0 &&
                        <L.ConnectionSvg width="100%" height="100%" viewBox={`0 0 ${boardSize.width} ${boardSize.height}`}>
                            <defs>
                                <marker id="arrow" viewBox="0 0 12 12" refX="11" refY="6" markerWidth="10" markerHeight="10" orient="auto">
                                    <path d="M 0 0 L 12 6 L 0 12 z" fill="#3b82f6" stroke="white" strokeWidth="1" />
                                </marker>
                                <marker id="arrow-active" viewBox="0 0 12 12" refX="11" refY="6" markerWidth="12" markerHeight="12" orient="auto">
                                    <path d="M 0 0 L 12 6 L 0 12 z" fill="#22c55e" stroke="white" strokeWidth="1" />
                                </marker>
                            </defs>
                            {connectionLines.map(line => (
                                <g key={line.id}>
                                    <L.ConnectionPath
                                        d={line.d}
                                        markerEnd={line.active ? "url(#arrow-active)" : "url(#arrow)"}
                                        $active={line.active}
                                        $speed={line.speed}
                                    />
                                    {line.speed > 0 && (
                                        <text
                                            x={line.labelX}
                                            y={line.labelY}
                                            fill="#374151"
                                            fontSize="11"
                                            fontWeight="600"
                                            textAnchor="middle"
                                            style={{pointerEvents: 'none', userSelect: 'none'}}
                                        >
                                            {line.speed.toFixed(2)}/s
                                        </text>
                                    )}
                                </g>
                            ))}
                            <defs>
                                <marker id="drag-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="10" markerHeight="10" orient="auto">
                                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#22c55e" />
                                </marker>
                            </defs>
                            {isDraggingConnection && dragConnectionFrom && dragLineEnd && activeSetup && (() => {
                                const fromNode = activeSetup.nodes.find(n => n.id === dragConnectionFrom);
                                if (!fromNode) return null;
                                const x1 = (fromNode.position.x / 100) * boardSize.width;
                                const y1 = (fromNode.position.y / 100) * boardSize.height;
                                return (
                                    <L.DragLine
                                        x1={x1}
                                        y1={y1}
                                        x2={dragLineEnd.x}
                                        y2={dragLineEnd.y}
                                        markerEnd="url(#drag-arrow)"
                                    />
                                );
                            })()}
                        </L.ConnectionSvg>}
                    {activeSetup?.nodes.map(node => {
                        const view = nodeViews.get(node.id);
                        const ready = view?.ready ?? node.type !== "assembler";
                        const recipe = view?.recipe;
                        const pulses = activeSetup ? inventoryPulses[activeSetup.id]?.filter(pulse => pulse.nodeId === node.id) ?? [] : [];
                        const connectionCount = activeSetup ? activeSetup.connections.filter(conn => conn.fromId === node.id).length : 0;
                        const hasOutputConnection = activeSetup ? activeSetup.connections.some(conn => conn.fromId === node.id) : false;

                        // Calculate missing ingredients
                        const missingIngredients: string[] = [];
                        if (node.type === "assembler" && recipe) {
                            const incomingConnections = activeSetup.connections.filter(conn => conn.toId === node.id);
                            const connectedItems = new Set(incomingConnections.map(conn => conn.itemId));
                            recipe.ingredients.forEach(ing => {
                                if (!connectedItems.has(ing.itemId)) {
                                    missingIngredients.push(ing.itemId);
                                }
                            });
                        }

                        // Calculate allocated output for source nodes
                        // Each setup gets an equal share of the total output
                        let allocatedOutput = 0;
                        let totalOutput = 0;
                        if (node.type === "source" && node.itemId) {
                            const stats = smelterStats.get(node.itemId);
                            if (stats) {
                                totalOutput = stats.totalOutputPerSecond;
                                // Divide by number of setups using this resource
                                if (stats.usageCount > 0) {
                                    const allocationPerSetup = stats.totalOutputPerSecond / stats.usageCount;

                                    // Count how many source nodes of this type exist in the current setup
                                    const sourceNodesInSetup = activeSetup.nodes.filter(
                                        n => n.type === "source" && n.itemId === node.itemId
                                    ).length;

                                    // Divide the setup allocation among all source nodes in this setup
                                    allocatedOutput = sourceNodesInSetup > 0 ? allocationPerSetup / sourceNodesInSetup : 0;
                                }
                            }
                        }

                        return <AssemblyNodeCard key={node.id}
                                                 node={node}
                                                 ready={ready}
                                                 recipe={recipe}
                                                 recipes={ASSEMBLY_RECIPE_LIST}
                                                 outputPerSecond={view?.outputPerSecond ?? 0}
                                                 maxOutputPerSecond={view?.maxOutputPerSecond ?? 0}
                                                 allocatedOutput={allocatedOutput}
                                                 totalOutput={totalOutput}
                                                 powerUse={view?.powerUse ?? 0}
                                                 fulfillment={view?.fulfillment ?? 100}
                                                 status={view?.status ?? "Idle"}
                                                 powerLimited={view?.powerLimited ?? false}
                                                 powerOffline={view?.powerOffline ?? false}
                                                 inventoryPulses={pulses}
                                                 active={view?.active ?? false}
                                                 connectionCount={connectionCount}
                                                 isInputHighlighted={highlightedInputNode === node.id}
                                                 hasOutputConnection={hasOutputConnection}
                                                 missingIngredients={missingIngredients}
                                                 ingredientInputRates={view?.ingredientInputRates ?? []}
                                                 onSetRecipe={setNodeRecipe}
                                                 onToggleFlag={toggleNodeFlag}
                                                 onStartDrag={startDragging}
                                                 onDelete={deleteNode}
                                                 onStartConnection={handleStartConnection}
                                                 onMouseEnterInput={handleMouseEnterInput}
                                                 onMouseLeaveInput={handleMouseLeaveInput}/>;
                    })}
                    </div>
                    <L.ZoomControls>
                        <L.ZoomButton onClick={handleZoomIn} disabled={zoom >= 3} title="Zoom in">+</L.ZoomButton>
                        <L.ZoomLevel>{Math.round(zoom * 100)}%</L.ZoomLevel>
                        <L.ZoomButton onClick={handleZoomOut} disabled={zoom <= 0.5} title="Zoom out">−</L.ZoomButton>
                    </L.ZoomControls>
                </L.BoardCanvas>
                    {powerOffline && <L.WarningBanner>
                        ⚡ Assembly requires electricity. Turn on generators in <a href="/production">Power production</a>.
                        <L.PowerHint>Assemblers stay paused until power is restored.</L.PowerHint>
                    </L.WarningBanner>}
                    {powerLimited && !powerOffline && <L.WarningBanner>
                        ⚠️ Limited power — machines are throttled.
                        <L.PowerHint title="Increase boilers/steam generators on the Power production page.">
                            Insufficient electricity to run at full capacity.
                        </L.PowerHint>
                    </L.WarningBanner>}
                    {throughputTotals.size > 0 && <L.ThroughputRow>
                        {[...throughputTotals.entries()].map(([itemId, perSecond]) => (
                            <L.ThroughputChip key={itemId} title="Items per second through this assembly">
                                <ItemIcon itemId={itemId} size={18} />
                                <span>{GameItems[itemId].name}: {perSecond.toFixed(2)}/s</span>
                            </L.ThroughputChip>
                        ))}
                    </L.ThroughputRow>}
                    {activeSetup && <AssemblyConnections
                        activeSetup={activeSetup}
                        connectionDraft={connectionDraft}
                        setConnectionDraft={setConnectionDraft}
                        connectionOptions={connectionOptions}
                        createConnection={createConnection}
                        removeConnection={removeConnection}
                        recipeLookup={recipeLookup}
                    />}
            </L.BoardShell>
        </div>
    </L.Page>;
}
